import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Compass, Play, Pause, Volume2, TreePine, Users, Leaf, HeartHandshake,
  Calendar, Clock, Upload, Camera, Share2, Download, X, Trash2, Loader2, MapPin, Sparkles,
} from "lucide-react";
import { getTrip, tripWelcomeAudioUrl, uploadTripPhoto, deleteTripPhoto, mediaUrl } from "../lib/adventureApi";

const brandFor = (market) => (market === "au" ? "Responsible Adventures Australia" : "Responsible Adventures");

function pad(n) { return String(n).padStart(2, "0"); }

function useClock() {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
}

// ---------------- Hero welcome audio player ----------------
function WelcomePlayer({ token, welcome }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(welcome.duration || 0);
  const ready = welcome.status === "ready";

  const toggle = () => {
    const a = ref.current; if (!a) return;
    if (playing) { a.pause(); } else { a.play().catch(() => toast.error("Couldn't play audio")); }
  };
  const seek = (e) => {
    const a = ref.current; if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * dur;
  };

  if (!ready) {
    return (
      <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/20 p-5 flex items-center gap-3" data-testid="welcome-pending">
        <Loader2 className="w-5 h-5 text-white animate-spin" />
        <p className="text-white/90 text-sm">Raj is recording your personal welcome — check back shortly.</p>
      </div>
    );
  }

  const pct = dur ? (cur / dur) * 100 : 0;
  return (
    <div className="rounded-xl bg-white/12 backdrop-blur-lg border border-white/25 p-4 sm:p-5" data-testid="welcome-player">
      <audio ref={ref} src={tripWelcomeAudioUrl(token)} preload="metadata"
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration || welcome.duration || 0)}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
      <div className="flex items-center gap-4">
        <button onClick={toggle} data-testid="welcome-play-btn"
          className="w-14 h-14 shrink-0 rounded-full bg-terracotta hover:bg-terracotta-hover text-white flex items-center justify-center transition-transform hover:scale-105 shadow-lg">
          {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-white font-display font-bold text-sm flex items-center gap-1.5"><Volume2 className="w-4 h-4" /> A message from Raj</p>
          <div onClick={seek} className="mt-2 h-2 rounded-full bg-white/25 cursor-pointer overflow-hidden" data-testid="welcome-progress">
            <div className="h-full bg-terracotta transition-[width] duration-150" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between mt-1 font-mono text-[10px] text-white/70">
            <span>{Math.floor(cur / 60)}:{pad(Math.floor(cur % 60))}</span>
            <span>{Math.floor(dur / 60)}:{pad(Math.floor(dur % 60))}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Evolutionary timeline ----------------
function Timeline({ startDate, endDate }) {
  useClock();
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  if (!start) return null;
  const now = new Date();
  const beforeTrip = now < start;
  const target = beforeTrip ? start : now;
  const base = beforeTrip ? start : start;
  const diffMs = beforeTrip ? start - now : now - start;
  const d = Math.floor(diffMs / 86400000);
  const h = Math.floor((diffMs % 86400000) / 3600000);
  const m = Math.floor((diffMs % 3600000) / 60000);
  const s = Math.floor((diffMs % 60000) / 1000);
  const finished = end && now > end;

  const cells = [["Days", d], ["Hrs", h], ["Min", m], ["Sec", s]];
  return (
    <div data-testid="trip-timeline" className="rounded-2xl bg-ink text-white p-6 sm:p-8">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta flex items-center gap-1.5">
        {beforeTrip ? <Calendar className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
        {beforeTrip ? "Countdown to departure" : finished ? "Time since your journey" : "You're on the trail"}
      </p>
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl mt-1.5">
        {beforeTrip ? "The adventure begins soon" : finished ? "A journey to remember" : "Living the adventure right now"}
      </h2>
      <div className="grid grid-cols-4 gap-3 sm:gap-4 mt-6" data-testid="timeline-clock">
        {cells.map(([label, val]) => (
          <div key={label} className="rounded-xl bg-white/8 border border-white/12 py-4 text-center">
            <div className="font-display font-extrabold text-3xl sm:text-4xl tabular-nums">{beforeTrip ? val : (label === "Days" ? val : pad(val))}</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/55 mt-1">{label}</div>
          </div>
        ))}
      </div>
      <p className="text-white/50 text-xs mt-4">
        {beforeTrip
          ? `Departing ${start.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}`
          : `Set off ${start.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}`}
      </p>
    </div>
  );
}

// ---------------- Impact tracker ----------------
const IMPACT_META = [
  { key: "trees", label: "Trees planted", icon: TreePine, color: "#0E9E6E" },
  { key: "jobs", label: "Local jobs supported", icon: Users, color: "#E23D28" },
  { key: "carbon_kg", label: "kg CO₂ offset", icon: Leaf, color: "#1F74C4" },
  { key: "crew_days", label: "Paid crew days", icon: HeartHandshake, color: "#C98A1E" },
];

function ImpactTracker({ impact }) {
  return (
    <div data-testid="impact-tracker">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-moss flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Your positive impact</p>
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl mt-1.5 text-ink">This booking makes a difference</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
        {IMPACT_META.map(({ key, label, icon: Icon, color }) => (
          <div key={key} data-testid={`impact-${key}`} className="rounded-xl bg-white border border-sand-dark p-5 text-center">
            <div className="w-11 h-11 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: `${color}1a` }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div className="font-display font-extrabold text-3xl text-ink mt-3 tabular-nums">{impact?.[key] ?? 0}</div>
            <div className="text-xs text-ink-secondary mt-1">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Living Memory gallery ----------------
const PHASES = [["before", "Before"], ["during", "During"], ["after", "After"]];

function Gallery({ token, photos, setPhotos }) {
  const [phase, setPhase] = useState("during");
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const filtered = useMemo(() => photos.filter((p) => p.phase === phase), [photos, phase]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const name = localStorage.getItem("trip_uploader") || "";
    let uploaderName = name;
    if (!uploaderName) {
      uploaderName = window.prompt("Add your name so the group knows who shared these (optional):") || "";
      if (uploaderName) localStorage.setItem("trip_uploader", uploaderName);
    }
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("phase", phase);
        fd.append("uploaded_by", uploaderName);
        const r = await uploadTripPhoto(token, fd);
        setPhotos((prev) => [r.photo, ...prev]);
      }
      toast.success(files.length > 1 ? `${files.length} photos added` : "Photo added to your diary");
    } catch { toast.error("Upload failed — please try again"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (p) => {
    if (!window.confirm("Remove this photo from the group diary?")) return;
    try { await deleteTripPhoto(token, p.id); setPhotos((prev) => prev.filter((x) => x.id !== p.id)); }
    catch { toast.error("Couldn't remove photo"); }
  };

  return (
    <div data-testid="memory-gallery">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Living memory gallery</p>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl mt-1.5 text-ink">Your shared diary</h2>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="gallery-upload-btn"
          className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 px-5 rounded-sm font-semibold text-sm transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Add photos
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="gallery-file-input" />
      </div>

      <div className="flex gap-2 mt-5">
        {PHASES.map(([id, label]) => {
          const count = photos.filter((p) => p.phase === id).length;
          return (
            <button key={id} onClick={() => setPhase(id)} data-testid={`gallery-phase-${id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${phase === id ? "bg-ink text-white border-ink" : "bg-white border-sand-dark text-ink-secondary hover:border-moss"}`}>
              {label} {count > 0 && <span className="opacity-60">· {count}</span>}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-6 rounded-xl border-2 border-dashed border-sand-dark bg-sand/40 py-14 text-center" data-testid="gallery-empty">
          <Camera className="w-8 h-8 text-ink-muted mx-auto" />
          <p className="text-ink-secondary mt-3">No {phase} photos yet — be the first to add a memory.</p>
        </div>
      ) : (
        <div className="mt-6 columns-2 sm:columns-3 lg:columns-4 gap-3 [&>*]:mb-3" data-testid="gallery-grid">
          {filtered.map((p) => (
            <div key={p.id} className="relative group break-inside-avoid rounded-lg overflow-hidden bg-sand" data-testid={`gallery-photo-${p.id}`}>
              <img src={mediaUrl(p.url)} alt={p.caption || "memory"} loading="lazy"
                onClick={() => setLightbox(p)} className="w-full h-auto object-cover cursor-zoom-in" />
              {(p.uploaded_by || p.caption) && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-white text-[11px]">
                  {p.caption && <div className="line-clamp-2">{p.caption}</div>}
                  {p.uploaded_by && <div className="opacity-75">— {p.uploaded_by}</div>}
                </div>
              )}
              <button onClick={() => remove(p)} data-testid={`gallery-delete-${p.id}`}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity hover:bg-terracotta">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[1200] bg-black/85 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button className="absolute top-5 right-5 text-white"><X className="w-7 h-7" /></button>
          <img src={mediaUrl(lightbox.url)} alt={lightbox.caption || "memory"} className="max-h-[88vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}

// ---------------- Ambassador share (Podcast Card) ----------------
function ShareTools({ trip }) {
  const [busy, setBusy] = useState(false);
  const brand = brandFor(trip.market);
  const start = trip.start_date ? new Date(trip.start_date) : null;
  const now = new Date();
  const daysToGo = start ? Math.ceil((start - now) / 86400000) : null;
  const countdownText = daysToGo == null ? "" : daysToGo > 0 ? `${daysToGo} days to go` : "On the journey";

  const drawCard = () => new Promise((resolve) => {
    const W = 1080, H = 1350;
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#171717"); g.addColorStop(0.55, "#2a1c16"); g.addColorStop(1, "#E23D28");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    // soft glow
    const rg = ctx.createRadialGradient(W * 0.8, H * 0.15, 40, W * 0.8, H * 0.15, 520);
    rg.addColorStop(0, "rgba(241,235,221,0.18)"); rg.addColorStop(1, "rgba(241,235,221,0)");
    ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);

    ctx.textAlign = "center";
    ctx.fillStyle = "#F1EBDD";
    ctx.font = "600 30px Manrope, sans-serif";
    ctx.fillText(brand.toUpperCase(), W / 2, 130);
    ctx.strokeStyle = "rgba(241,235,221,0.4)"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(W / 2 - 90, 160); ctx.lineTo(W / 2 + 90, 160); ctx.stroke();

    // countdown badge
    if (countdownText) {
      ctx.fillStyle = "#E23D28";
      ctx.font = "800 120px 'Cabinet Grotesk', Manrope, sans-serif";
      ctx.fillText(daysToGo > 0 ? String(daysToGo) : "•", W / 2, 400);
      ctx.fillStyle = "#F1EBDD";
      ctx.font = "700 44px Manrope, sans-serif";
      ctx.fillText(daysToGo > 0 ? "DAYS TO GO" : "ON THE TRAIL", W / 2, 470);
    }

    // trip name (wrap)
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 76px 'Cabinet Grotesk', Manrope, sans-serif";
    const words = (trip.trip_name || "").split(" ");
    let line = "", y = 640; const maxW = W - 160;
    const lines = [];
    words.forEach((w) => {
      const test = line ? line + " " + w : w;
      if (ctx.measureText(test).width > maxW) { lines.push(line); line = w; } else line = test;
    });
    if (line) lines.push(line);
    lines.slice(0, 3).forEach((ln) => { ctx.fillText(ln, W / 2, y); y += 88; });

    ctx.fillStyle = "rgba(255,255,255,0.8)";
    ctx.font = "500 38px Manrope, sans-serif";
    if (trip.destination) ctx.fillText(trip.destination, W / 2, y + 6);

    // audio prompt
    ctx.fillStyle = "#F1EBDD";
    ctx.font = "600 34px Manrope, sans-serif";
    ctx.fillText("🎧  Listen to Raj's welcome message", W / 2, H - 220);

    // link
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.font = "500 30px 'JetBrains Mono', monospace";
    const link = `${window.location.host}/trip/${trip.token}`;
    ctx.fillText(link, W / 2, H - 150);

    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.font = "500 26px Manrope, sans-serif";
    ctx.fillText("Beyond the ordinary · Responsible travel", W / 2, H - 90);

    c.toBlob((blob) => resolve(blob), "image/png");
  });

  const share = async () => {
    setBusy(true);
    try {
      const blob = await drawCard();
      const file = new File([blob], `${trip.token}-podcast-card.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: trip.trip_name, text: `${countdownText} — ${trip.trip_name} with ${brand}` });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = file.name; a.click();
        URL.revokeObjectURL(url);
        toast.success("Podcast card downloaded — share it anywhere!");
      }
    } catch { toast.error("Couldn't create the card"); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const blob = await drawCard();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${trip.token}-podcast-card.png`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Podcast card downloaded");
    } catch { toast.error("Couldn't create the card"); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/trip/${trip.token}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        throw new Error("clipboard unavailable");
      }
      toast.success("Trip link copied");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Trip link copied");
      } catch {
        toast.error("Couldn't copy — link: " + url);
      }
    }
  };

  return (
    <div data-testid="share-tools" className="rounded-2xl bg-gradient-to-br from-terracotta to-[#b8301f] text-white p-6 sm:p-8">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70 flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" /> Ambassador share tools</p>
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl mt-1.5">Share the countdown</h2>
      <p className="text-white/80 text-sm mt-2 max-w-md">Generate a beautiful Podcast Card for Instagram, WhatsApp or Facebook — it shows your countdown and links back to Raj's welcome.</p>
      <div className="flex flex-wrap gap-3 mt-5">
        <button onClick={share} disabled={busy} data-testid="share-card-btn"
          className="inline-flex items-center gap-2 bg-white text-terracotta h-11 px-5 rounded-sm font-semibold text-sm hover:bg-white/90 transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share podcast card
        </button>
        <button onClick={download} disabled={busy} data-testid="download-card-btn"
          className="inline-flex items-center gap-2 border border-white/40 text-white h-11 px-5 rounded-sm font-semibold text-sm hover:bg-white/10 transition-colors disabled:opacity-60">
          <Download className="w-4 h-4" /> Download
        </button>
        <button onClick={copyLink} data-testid="copy-link-btn"
          className="inline-flex items-center gap-2 border border-white/40 text-white h-11 px-5 rounded-sm font-semibold text-sm hover:bg-white/10 transition-colors">
          <MapPin className="w-4 h-4" /> Copy trip link
        </button>
      </div>
    </div>
  );
}

// ---------------- Page ----------------
export default function TripDashboard() {
  const { token } = useParams();
  const [trip, setTrip] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getTrip(token).then((d) => { setTrip(d); setPhotos(d.photos || []); }).catch(() => setNotFound(true));
  }, [token]);

  // poll while the welcome is still generating
  useEffect(() => {
    if (!trip || trip.welcome?.status === "ready") return;
    if (!["generating", "idle"].includes(trip.welcome?.status)) return;
    const t = setInterval(() => {
      getTrip(token).then((d) => { if (d.welcome?.status === "ready") { setTrip(d); clearInterval(t); } }).catch(() => {});
    }, 6000);
    return () => clearInterval(t);
  }, [trip, token]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center p-6 text-center" data-testid="trip-not-found">
        <div>
          <Compass className="w-10 h-10 text-terracotta mx-auto" />
          <h1 className="font-display font-extrabold text-2xl mt-4">Trip not found</h1>
          <p className="text-ink-secondary mt-2">This link may be incorrect or the trip has been removed.</p>
        </div>
      </div>
    );
  }
  if (!trip) {
    return <div className="min-h-screen bg-base flex items-center justify-center text-ink-secondary">
      <Compass className="w-6 h-6 animate-spin text-terracotta mr-2" /> Opening your Living Memory…
    </div>;
  }

  const brand = brandFor(trip.market);
  const hero = trip.hero_image_url ? mediaUrl(trip.hero_image_url) : null;

  return (
    <div className="min-h-screen bg-base text-ink" data-testid="trip-dashboard">
      {/* slim header */}
      <header className="sticky top-0 z-[60] bg-base/90 backdrop-blur-md border-b border-sand-dark">
        <div className="max-w-6xl mx-auto flex items-center gap-2.5 px-5 sm:px-8 h-[60px]">
          <div className="w-8 h-8 bg-terracotta rounded-sm flex items-center justify-center"><Compass className="w-4.5 h-4.5 text-white" /></div>
          <div className="leading-none">
            <div className="font-display font-extrabold text-[13px] tracking-tight">{brand.toUpperCase()}</div>
            <div className="font-mono text-[8px] tracking-[0.25em] uppercase text-moss mt-0.5">Your Living Memory</div>
          </div>
        </div>
      </header>

      {/* HERO WELCOME */}
      <section className="relative overflow-hidden" data-testid="hero-welcome">
        {hero
          ? <img src={hero} alt={trip.name} className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0 bg-gradient-to-br from-[#221c17] via-[#33261d] to-[#4a2c20]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <div className="grid lg:grid-cols-[1.3fr,1fr] gap-8 items-end">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terracotta">Welcome, {trip.name}</p>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.02] mt-3" data-testid="hero-trip-name">
                {trip.trip_name}
              </h1>
              <p className="text-white/75 mt-3 flex items-center gap-2 text-sm">
                {trip.destination && <><MapPin className="w-4 h-4" /> {trip.destination}</>}
                {trip.travellers ? <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {trip.travellers} travellers</span> : null}
              </p>
            </div>
            <div className="flex items-end gap-4">
              <img src="/podcast/raj.jpeg" alt="Raj" className="w-16 h-16 rounded-full object-cover border-2 border-terracotta shadow-lg shrink-0" />
              <div className="flex-1"><WelcomePlayer token={token} welcome={trip.welcome} /></div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16 space-y-14 sm:space-y-20">
        <Timeline startDate={trip.start_date} endDate={trip.end_date} />
        <ImpactTracker impact={trip.impact} />
        <Gallery token={token} photos={photos} setPhotos={setPhotos} />
        <ShareTools trip={trip} />
      </main>

      <footer className="border-t border-sand-dark py-8 text-center text-xs text-ink-muted">
        © {new Date().getFullYear()} {brand} · Beyond the ordinary
      </footer>
    </div>
  );
}
